<template>
    <b-modal id="locale-string-detail-modal"
             :title="modalTitle"
             hide-footer
             size="xl"
             modal-class="default-modal"
             @hide="onHide"
             @hidden="onHidden"
    >
        <div class="action-btns">
            <template v-if="mode === 'readOnly'">
                <div style="height: 32px"/>
            </template>
            <template v-else-if="mode === 'detail'">
                <b-button variant="outline-primary"
                          :disabled="!detailData.id"
                          @click="changeMode('edit')"
                >
                    {{ $t('수정') }}
                </b-button>
            </template>
            <template v-else-if="mode === 'add'">
                <b-button variant="outline-primary"
                          form="locale-string-form"
                          type="submit"
                >
                    {{ $t('저장') }}
                </b-button>
            </template>
            <template v-else-if="mode === 'edit'">
                <b-button variant="outline-danger"
                          @click="confirmDelete"
                >
                    {{ $t('삭제') }}
                </b-button>
                <b-button variant="outline-primary"
                          form="locale-string-form"
                          type="submit"
                >
                    {{ $t('저장') }}
                </b-button>
                <b-button variant="outline-secondary"
                          @click="cancelEdit"
                >
                    {{ $t('취소') }}
                </b-button>
            </template>
        </div>
        <div class="tabs-container">
            <b-tabs v-model="tabIndex" class="default-tabs" :class="{'with-actions': mode !== 'add'}" content-class="mt-3">
                <b-tab :title="$t('기본 정보')">
                    <template v-if="loading">
                        <b-skeleton-img no-aspect class="empty-content"></b-skeleton-img>
                    </template>
                    <PageEmptyComponent v-else-if="loadError && mode !== 'add'" compact :error="loadError" @retry="fetchDetailData"/>
                    <template v-else>
                        <LocaleStringDetail v-if="mode === 'detail' || mode ==='readOnly'" :detail-data="detailData"/>
                        <LocaleStringForm v-else ref="localeStringForm" :mode="mode" :detail-data="detailData"
                                          @updateDetailData="updateDetailData"
                                          @dirty="v => formDirty = v"
                        />
                    </template>
                </b-tab>
            </b-tabs>
        </div>
    </b-modal>
</template>

<script>
import PageEmptyComponent from '@shared/components/admin/components/common/PageEmptyComponent.vue';
import { getErrorMessage } from '@core/utils/api/errorMessage';
import {deleteLocalizedString, getLocalizedString} from '@shared_utils/api/projectSchema';

import LocaleStringDetail
    from '@shared/components/admin/pages/Management/ProjectSchema/LocaleString/LocaleStringDetail.vue';
import LocaleStringForm
    from '@shared/components/admin/pages/Management/ProjectSchema/LocaleString/LocaleStringForm.vue';
import unsavedChangesGuard from '@shared/mixin/unsaved-changes-guard';

const CLOSE_CONFIRM = {title: '저장하지 않은 입력', message: '입력한 내용이 저장되지 않았습니다. 닫을까요?'};
const EDIT_CANCEL_CONFIRM = {title: '저장하지 않은 입력', message: '수정한 내용이 저장되지 않았습니다. 수정을 취소할까요?'};

export default {
    name: 'LocaleStringModal',
    components: {
        PageEmptyComponent,
        LocaleStringForm,
        LocaleStringDetail,
    },
    mixins: [unsavedChangesGuard],
    props: {
        detailId: {
            type: [Number, String, null],
            default: null,
        }
    },
    data() {
        return {
            mode: 'detail', // detail | add | edit | readOnly
            tabIndex: 1,
            detailData: {id: null},
            loading: false,
            loadError: '',
            formDirty: false,
            confirmingClose: false,
        };
    },
    computed: {
        // 등록·수정 폼에서 값이 바뀌었는지 (폼이 @dirty 로 알려 준다)
        hasUnsavedChanges() {
            return (this.mode === 'add' || this.mode === 'edit') && this.formDirty;
        },
        modalTitle() {
            if (this.mode === 'add') {
                return this.$t('다국어 문자열 등록');
            } else if (this.mode === 'edit') {
                return this.$t('다국어 문자열 수정');
            } else {
                return this.$t('다국어 문자열 상세');
            }
        },
        readOnly() {
            return this.mode === 'readOnly';
        }
    },
    watch: {
        detailId: {
            immediate: true,
            handler(newVal) {
                if (newVal) {
                    this.fetchDetailData();
                } else {
                    this.detailData = {id: null};
                }
            }
        }
    },
    methods: {
        changeMode(mode) {
            this.mode = mode;
            this.tabIndex = 1;
        },
        async cancelEdit() {
            if (!(await this.confirmDiscardChanges(EDIT_CANCEL_CONFIRM))) return;
            this.changeMode('detail');
        },
        // X·ESC·바깥 클릭·hide() 모두 이리로 온다. 입력이 바뀌었으면 확인 후 닫는다(화면 정리는 hidden 에서)
        onHide(bvEvent) {
            if (!this.hasUnsavedChanges && !this.confirmingClose) return;
            bvEvent.preventDefault();
            if (this.confirmingClose) return;
            this.confirmingClose = true;
            this.confirmDiscardChanges(CLOSE_CONFIRM).then((ok) => {
                this.confirmingClose = false;
                if (ok) this.closeModal();
            });
        },
        onHidden() {
            this.mode = 'detail';
        },
        // 저장·삭제 뒤처럼 코드에서 닫을 때는 미저장 확인을 묻지 않는다
        closeModal() {
            this.formDirty = false;
            this.$bvModal.hide('locale-string-detail-modal');
        },
        async fetchDetailData() {
            try {
                this.loading = true;
                this.loadError = '';
                this.detailData = {id: null};
                const res = await getLocalizedString(this.detailId);
                this.detailData = res.data;
            } catch (e) {
                console.error(e);
                this.loadError = e.response && e.response.status === 404
                    ? '존재하지 않는 항목입니다.'
                    : getErrorMessage(e, '상세 정보를 불러오지 못했습니다.');
                this.$EventBus.$emit('toast-failure', this.$t('상세 조회 실패'), this.loadError);
            } finally {
                this.loading = false;
            }
        },
        updateDetailData(detailData) {
            if (this.mode === 'add') {
                this.$emit('addData', detailData);
                this.closeModal();
            } else if (this.mode === 'edit') {
                this.$emit('updateDetailData', detailData);
                this.detailData = detailData;
                this.changeMode('detail');
            }
        },
        confirmDelete() {
            this.$showModal({
                type: 'confirm',
                state: 'warning',
                title: this.$t('다국어 문자열 삭제'),
                message: this.$t('현 다국어 문자열을 삭제하시겠습니까?')
            })
                .then((v) => {
                    if (v) {
                        this.deleteCondition();
                    }
                });
        },
        deleteCondition() {
            deleteLocalizedString(this.detailData.key)
                .then(() => {
                    this.$EventBus.$emit('toast-success', this.$t('다국어 문자열 삭제'), this.$t('다국어 문자열이 성공적으로 삭제되었습니다.'));
                    this.$emit('deleteData', this.detailData.id);
                    this.closeModal();
                })
                .catch((error) => {
                    console.error(error);
                    // if(error?.response.status === 400){
                    //     const errorMsg = error?.response?.data;
                    //     if(errorMsg?.includes('Cannot delete locale-string. It is used by rule groups:')){
                    //         const match = errorMsg.match(/\[([^\]]*)\]/);
                    //
                    //         if (match) {
                    //             const h = this.$createElement;
                    //             const ruleGroup = match[1].replace(/'/g, '').split(', ');
                    //             const badges = ruleGroup.map(name => {
                    //                 return h(
                    //                     'b-badge',
                    //                     {
                    //                         props: { variant: 'warning' },
                    //                         class: 'mx-1 f-90'
                    //                     },
                    //                     name
                    //                 );
                    //             });
                    //
                    //             const messageVNode = h(
                    //                 'div',
                    //                 [
                    //                     h('p', '해당 조건은 아래 규칙 그룹에서 사용 중이므로 삭제할 수 없습니다.'),
                    //                     h('div', { class: 'mt-3' }, badges)
                    //                 ]
                    //             );
                    //
                    //            
                    //             this.$showModal({
                    //                 type: 'alert',
                    //                 state: 'error',
                    //                 title: this.$t('조건 삭제 오류'),
                    //                 message: messageVNode
                    //             });
                    //         }
                    //         return;
                    //     }
                    // }
                    this.$EventBus.$emit('toast-failure', this.$t('다국어 문자열 삭제'), this.$t('다국어 문자열 삭제 중 오류가 발생했습니다. 개발 팀에 문의해주세요.'));
                });
        }
    },
};
</script>

<style scoped lang="scss">

</style>
